import {useEffect, useState} from 'react';
import {
  BreadSDKConfig,
  BreadPlacement,
  BreadPaymentsSDK,
  CustomerData,
  BreadPrescreenRequest,
  BreadEventType,
  BreadApplicationState,
} from '../models/BreadSDK';
import {useDispatch, useSelector} from 'Provider';
import {
  AuthActions,
  LoginInfo,
  Selectors,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  allowBreadSDKNoGLBA,
  allowBreadSDKOriginalGLBA,
  allowBreadSDKNoPrescreen,
} from 'store/target/target.selectors';
import {isProduction} from 'services/api/api';
import {placementType} from 'utils';
import {useShouldTriggerBread} from './useShouldTriggerBread';
import {ProfileActions} from 'store/profile';
import {DateTime} from 'luxon';
import Cookies from 'js-cookie';

// Module-level state that persists across re-renders and component unmounts
let breadApplyNowState: BreadApplicationState = 'HIDDEN';
let breadTimeoutID: NodeJS.Timeout | undefined;
let currentPromiseResolver: ((value: void | PromiseLike<void>) => void) | null =
  null;
let currentPromiseRejecter: ((reason?: any) => void) | null = null;

// Export for read access from other components
export {breadApplyNowState};

export const useBreadSDK = (isLoggedIn: boolean, isAppLoad: boolean) => {
  const maxWaitTime = 10000;

  // This logic is done because the prescreen target test is automatically injected in lower environments for testing
  // And the no prescreen target test will be a way to overwrite it
  // Similar with GLBA setting, original glba test will control whether no glba overwrite is applied

  const breadSDKNoPrescreen = useSelector(allowBreadSDKNoPrescreen);
  const allowPrescreen = !breadSDKNoPrescreen;
  const breadSDKNoGLBA = useSelector(allowBreadSDKNoGLBA);
  const breadSDKUseOriginalGLBA = useSelector(allowBreadSDKOriginalGLBA);

  const forceGLBAFalse = breadSDKNoGLBA && !breadSDKUseOriginalGLBA;
  const readyForPrescreen = useWidgetState(
    Selectors.isReadyForPrescreen
  ) as boolean;
  const [sdkInstance, setSdkInstance] = useState<BreadPaymentsSDK | null>(
    window.BreadPayments?.init ? window.BreadPayments : null
  );
  const logininfo = useWidgetState(Selectors.loginInfoSelector) as LoginInfo;
  const crNumber = useWidgetState(Selectors.userAccountIdSelector) as string;
  const cachedDeclineResponse =
    Cookies.get('cr_visa_response_' + crNumber.slice(-4)) === 'NO';

  const useSDK = useShouldTriggerBread();
  const widgetDispatch = useWidgetDispatch();
  const dispatch = useDispatch();

  // Create a promise that resolves when sdkInstance becomes available
  const waitForSdkInstance = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      // Check for sdkInstance at regular intervals
      const checkInstance = () => {
        // If sdkInstance exists, resolve the promise
        if (sdkInstance) {
          resolve(sdkInstance);
          return;
        }
        // If we've waited too long, reject the promise
        if (Date.now() - startTime > maxWaitTime) {
          reject(
            new Error('Timed out waiting for Bread SDK instance to initialize')
          );
          return;
        }
        // Otherwise, check again after a short delay
        setTimeout(checkInstance, 100);
      };
      // Start checking
      checkInstance();
    });
  };

  const clearTimeoutID = () => {
    if (breadTimeoutID) {
      clearTimeout(breadTimeoutID);
      breadTimeoutID = undefined;
    }
  };

  const setup = async (includeCustomerData: boolean) => {
    if (!sdkInstance) {
      if (!isProduction) {
        console.error('Cannot start set up: SDK not initialized');
      }
      return;
    }
    // check if setupConfig already exists
    const setupConfigInWindow = window.BreadPayments.setupConfig;
    if (!!setupConfigInWindow && !!setupConfigInWindow.loyaltyID) {
      if (includeCustomerData) {
        if (setupConfigInWindow.buyer?.familyName) {
          return;
        }
      } else {
        return;
      }
    }
    const setupConfig: BreadSDKConfig = {
      loyaltyID: crNumber,
      env: isProduction ? 'PROD' : 'STAGE',
      storeNumber: '',
    };
    if (includeCustomerData) {
      const customerData = getCustomerData();
      setupConfig.buyer = {
        givenName: customerData.firstName,
        familyName: customerData.lastName,
        birthDate: customerData.dob
          ? DateTime.fromFormat(customerData.dob, 'MM/dd/yyyy').toFormat(
              'yyyy-MM-dd'
            )
          : '',
        email: customerData.email,
        phone: customerData.phone,
        billingAddress: {
          address1: customerData.address1,
          address2: customerData.address2,
          country: customerData.country,
          locality: customerData.city,
          region: customerData.state,
          postalCode: customerData.zip,
        },
      };
    }

    await sdkInstance.setup(setupConfig);
  };

  const initializeSDK = async () => {
    // Create a promise that resolves when sdkInstance becomes available
    const waitForSdkInit = (): Promise<any> => {
      return new Promise((resolve, reject) => {
        const startTime = Date.now();

        // Check for init at regular intervals
        const checkInit = () => {
          // If sdkInstance exists, resolve the promise
          if (window.BreadPayments?.init) {
            resolve(window.BreadPayments);
            return;
          }
          // If we've waited too long, reject the promise
          if (Date.now() - startTime > maxWaitTime) {
            reject(
              new Error(
                'Timed out waiting for Bread SDK instance to initialize'
              )
            );
            return;
          }
          // Otherwise, check again after a short delay
          setTimeout(checkInit, 100);
        };
        // Start checking
        checkInit();
      });
    };

    try {
      // Wait for the SDK init to be available
      const sdk = await waitForSdkInit();
      if (sdk) {
        setSdkInstance(sdk);
      } else {
        if (!isProduction) {
          console.error('Timeout waiting for sdk');
        }
        clearTimeoutID();
      }
    } catch (error) {
      if (!isProduction) {
        console.error('Error in initialize sdk:', error);
      }
      clearTimeoutID();
    }
  };

  const submitPrescreen = () => {
    if (!sdkInstance) {
      if (!isProduction) {
        console.error('Cannot start prescreen: SDK not initialized');
      }
      return;
    }
    try {
      const prescreenRequest: BreadPrescreenRequest = {
        financingType: 'card',
        locationType: placementType,
      };
      sdkInstance.on(
        'CARD:OFFER_RESPONSE',
        function (this: {event: BreadEventType}, data) {
          if (data === 'NO') {
            Cookies.set('cr_visa_response_' + crNumber.slice(-4), 'NO', {
              expires: 30, // 30 days, update as needed
            });
          }
          if (!isProduction) {
            console.log('Bread SDK Event:', this.event, data);
          }
        }
      );
      sdkInstance.submitRtps(prescreenRequest);
    } catch (error) {
      if (!isProduction) {
        console.error('Prescreen submission error:', error);
      }
    }
  };

  const apply = (): Promise<void> => {
    const promise = new Promise<void>((resolve, reject) => {
      // Store the current Promise's resolver functions at module level
      currentPromiseResolver = resolve;
      currentPromiseRejecter = reject;

      const executeApply = async () => {
        try {
          breadApplyNowState = 'LOADING';

          clearTimeoutID();

          dispatch(ProfileActions.setBreadSDKError(false));

          // Wait for the SDK instance to be available
          const sdk = await waitForSdkInstance();
          if (sdk) {
            if (sdkInstance) {
              const emitterEvent =
                window.BreadPayments.publicEventEmitter?.emitter?.event;
              const eventListenerExists =
                !!emitterEvent && emitterEvent === 'CARD:SHOW_OVERLAY_CONTENT';
              if (!eventListenerExists) {
                sdkInstance.on(
                  'CARD:SHOW_OVERLAY_CONTENT',
                  function (this: {event: BreadEventType}, data) {
                    if (!isProduction) {
                      console.log('Bread SDK Event:', this.event, data);
                    }
                    breadApplyNowState = 'OPEN';
                    clearTimeoutID();

                    if (currentPromiseResolver) {
                      currentPromiseResolver();
                      currentPromiseResolver = null;
                      currentPromiseRejecter = null;
                    }
                  }
                );
              }
            }

            await setup(false);
            const placement: BreadPlacement = {
              financingType: 'card',
              locationType: placementType,
              placementId: 'iframe-overlay-caesars',
            };

            sdk.openExperienceForPlacement(placement);

            // Set up timeout that will reject the promise
            const timeout = setTimeout(() => {
              if (breadApplyNowState !== 'OPEN') {
                dispatch(ProfileActions.setBreadSDKError(true));
                breadApplyNowState = 'HIDDEN';

                if (currentPromiseRejecter) {
                  currentPromiseRejecter(new Error('Bread SDK timeout'));
                  currentPromiseResolver = null;
                  currentPromiseRejecter = null;
                }
              }
            }, maxWaitTime);
            breadTimeoutID = timeout;
          } else {
            clearTimeoutID();
            breadApplyNowState = 'HIDDEN';
            dispatch(ProfileActions.setBreadSDKError(true));
            reject(new Error('SDK not available'));
          }
        } catch (error) {
          clearTimeoutID();
          breadApplyNowState = 'HIDDEN';
          dispatch(ProfileActions.setBreadSDKError(true));
          reject(error);
        }
      };

      // Execute the async function
      executeApply();
    });

    return promise;
  };

  const prescreenProcess = async () => {
    try {
      // Check for incomplete address information
      const customerData = getCustomerData();
      const hasIncompleteAddress =
        customerData.city === 'ONL SIGNUP-NO CITY' ||
        customerData.address1 === 'ONL SIGNUP-NO ADDR' ||
        !customerData.city.trim() ||
        !customerData.address1.trim();

      if (hasIncompleteAddress) {
        return;
      }

      await setup(true);
      submitPrescreen();
    } catch (err) {
      if (!isProduction) {
        console.error('Error in bread prescreen process: ', err);
      }
    }
  };

  const getCustomerData = (): CustomerData => {
    const address = logininfo.addresses?.address.find(
      a => a.location === 'Home'
    );
    const homephone = logininfo.phonenumbers?.find(n => n.location === 'Home')
      ?.phonenumber;
    const cellphone = logininfo.mobilenumber?.number;
    const phoneNumber = homephone ? homephone : cellphone ? cellphone : '';

    const customerData = {
      firstName: logininfo.firstname ?? '',
      lastName: logininfo.lastname ?? '',
      email: logininfo.email ?? '',
      phone: phoneNumber,
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      city: address?.city ?? '',
      state: address?.stateprovince ?? '',
      zip: address?.zipcode ?? '',
      dob: logininfo.dateofbirth ?? '',
      country: address?.country ?? '',
    };
    return customerData;
  };

  useEffect(() => {
    if (!sdkInstance && useSDK) {
      initializeSDK();
    }
  }, [useSDK]);

  useEffect(() => {
    try {
      const shouldTryPrescreen =
        !cachedDeclineResponse &&
        isLoggedIn &&
        !!sdkInstance &&
        useSDK &&
        isAppLoad &&
        allowPrescreen &&
        readyForPrescreen;
      if (shouldTryPrescreen) {
        const noGLBA =
          logininfo.preferences?.glbapreference === 'false' || forceGLBAFalse;

        if (noGLBA) {
          prescreenProcess();
        }
        widgetDispatch(AuthActions.resetReadyForPrescreen());
      }
    } catch (err) {
      if (!isProduction) {
        console.error('Error running Bread SDK: ', err);
      }
    }
  }, [
    isLoggedIn,
    sdkInstance,
    useSDK,
    allowPrescreen,
    readyForPrescreen,
    forceGLBAFalse,
    cachedDeclineResponse,
  ]);

  // Hook Interface
  return {
    apply,
  };
};
